// src/pages/ContactsPage.js
import { useState, useEffect } from 'react';
import api from '../utils/api';

const RELATIONSHIP_STYLES = {
  prospect: { color: 'var(--text-muted)',  label: 'Prospect'  },
  warm:     { color: 'var(--blue)',        label: 'Warm'      },
  active:   { color: 'var(--green)',       label: 'Active'    },
  past:     { color: 'var(--orange)',      label: 'Past'      },
  partner:  { color: 'var(--accent)',      label: 'Partner'   },
};

const COMPANY_TYPES = [
  { value: 'mine_operator',  label: 'Mine Operator'  },
  { value: 'epc',            label: 'EPC/Construction'},
  { value: 'government',     label: 'Government'     },
  { value: 'indigenous_org', label: 'Indigenous Org' },
  { value: 'consulting',     label: 'Consulting'     },
  { value: 'other',          label: 'Other'          },
];

const ROLE_TYPES = [
  { value: 'procurement',         label: 'Procurement'          },
  { value: 'pm',                  label: 'Project Manager'      },
  { value: 'estimator',           label: 'Estimator'            },
  { value: 'indigenous_coordinator', label: 'Indigenous Coordinator' },
  { value: 'executive',           label: 'Executive'            },
  { value: 'engineer',            label: 'Engineer'             },
  { value: 'other',               label: 'Other'                },
];

const EMPTY_FORM = {
  name:'', title:'', company:'', company_type:'other', email:'', phone:'',
  province:'', relationship:'prospect', role_type:'other', notes:'', last_contact:'',
};

export default function ContactsPage() {
  const [contacts, setContacts] = useState([]);
  const [stats, setStats]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm]         = useState({ ...EMPTY_FORM });
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState('');
  const [search, setSearch]     = useState('');
  const [filterRel, setFilterRel] = useState('');

  useEffect(() => {
    api.get('/contacts').then(data => {
      setContacts(data.contacts || []);
      setStats(data.stats || []);
    }).finally(() => setLoading(false));
  }, []);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true); setError('');
    try {
      const created = await api.post('/contacts', form);
      setContacts(c => [created, ...c]);
      setShowForm(false);
      setForm({ ...EMPTY_FORM });
    } catch (err) {
      setError(err.message);
    } finally { setSaving(false); }
  };

  const filtered = contacts.filter(c => {
    if (filterRel && c.relationship !== filterRel) return false;
    if (search) {
      const s = search.toLowerCase();
      return c.name.toLowerCase().includes(s) ||
             (c.company || '').toLowerCase().includes(s) ||
             (c.email || '').toLowerCase().includes(s);
    }
    return true;
  });

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Contacts</div>
          <div style={{ fontSize:'0.85rem', color:'var(--text-muted)', marginTop:'0.2rem' }}>
            {contacts.length} contacts in CRM
          </div>
        </div>
        <button className="btn btn-primary" onClick={() => setShowForm(true)}>
          <PlusIcon /> Add Contact
        </button>
      </div>

      {/* Relationship filter pills */}
      <div style={{ display:'flex', gap:'0.5rem', marginBottom:'1rem', flexWrap:'wrap' }}>
        {[{value:'', label:'All'}, ...Object.entries(RELATIONSHIP_STYLES).map(([v,s]) => ({value:v, label:s.label}))].map(r => {
          const count = r.value ? stats.find(s => s.relationship === r.value)?.count : contacts.length;
          const isActive = filterRel === r.value;
          const color = r.value ? RELATIONSHIP_STYLES[r.value]?.color : 'var(--text-secondary)';
          return (
            <button key={r.value} onClick={() => setFilterRel(r.value)}
              style={{ padding:'0.3rem 0.75rem', borderRadius:20, border:'1px solid', fontSize:'0.78rem', cursor:'pointer',
                borderColor: isActive ? color : 'var(--border)',
                background: isActive ? color + '22' : 'transparent',
                color: isActive ? color : 'var(--text-secondary)',
              }}>
              {r.label} {count ? `(${count})` : ''}
            </button>
          );
        })}
      </div>

      <input type="text" placeholder="Search contacts…" value={search}
        onChange={e => setSearch(e.target.value)} style={{ maxWidth:280, marginBottom:'1.25rem' }} />

      <div className="card" style={{ padding:0 }}>
        {loading ? (
          <div style={{ padding:'3rem', textAlign:'center', color:'var(--text-muted)' }}>Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="empty-state">
            <PeopleIcon />
            <h3>No contacts yet</h3>
            <p style={{ fontSize:'0.85rem' }}>Add procurement contacts, PMs, and key relationships.</p>
            <button className="btn btn-primary" onClick={() => setShowForm(true)}>Add Contact</button>
          </div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Company</th>
                <th>Role</th>
                <th>Contact</th>
                <th>Province</th>
                <th>Relationship</th>
                <th>Last Contact</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(c => {
                const rel = RELATIONSHIP_STYLES[c.relationship] || {};
                return (
                  <tr key={c.id}>
                    <td>
                      <div style={{ fontWeight:600 }}>{c.name}</div>
                      {c.title && <div style={{ fontSize:'0.75rem', color:'var(--text-muted)' }}>{c.title}</div>}
                    </td>
                    <td>
                      <div style={{ fontSize:'0.85rem' }}>{c.company || '—'}</div>
                      <div style={{ fontSize:'0.72rem', color:'var(--text-muted)' }}>
                        {COMPANY_TYPES.find(t => t.value === c.company_type)?.label}
                      </div>
                    </td>
                    <td style={{ fontSize:'0.82rem', color:'var(--text-secondary)' }}>
                      {ROLE_TYPES.find(r => r.value === c.role_type)?.label || '—'}
                    </td>
                    <td>
                      {c.email && <div style={{ fontSize:'0.8rem' }}><a href={`mailto:${c.email}`} style={{ color:'var(--blue)' }}>{c.email}</a></div>}
                      {c.phone && <div style={{ fontSize:'0.78rem', color:'var(--text-muted)' }}>{c.phone}</div>}
                    </td>
                    <td style={{ fontSize:'0.82rem', color:'var(--text-secondary)' }}>{c.province || '—'}</td>
                    <td>
                      <span style={{ fontSize:'0.75rem', fontWeight:700, color: rel.color, background: (rel.color||'') + '22', padding:'0.15rem 0.5rem', borderRadius:10 }}>
                        {rel.label}
                      </span>
                    </td>
                    <td style={{ fontSize:'0.8rem', color:'var(--text-muted)' }}>
                      {c.last_contact ? new Date(c.last_contact + 'T00:00:00').toLocaleDateString('en-CA', { dateStyle:'medium' }) : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {showForm && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowForm(false)}>
          <div className="modal" style={{ maxWidth:640 }}>
            <div className="modal-header">
              <span className="modal-title">Add Contact</span>
              <button className="btn btn-ghost" onClick={() => setShowForm(false)}>✕</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body" style={{ display:'flex', flexDirection:'column', gap:'1rem' }}>
                {error && <div style={{ background:'var(--red-dim)', border:'1px solid var(--red)', borderRadius:'var(--radius-sm)', padding:'0.75rem', fontSize:'0.85rem', color:'var(--red)' }}>{error}</div>}
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'1rem' }}>
                  <div className="form-group">
                    <label className="form-label">Full Name *</label>
                    <input value={form.name} onChange={e => set('name', e.target.value)} placeholder="Jane Smith" required />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Title</label>
                    <input value={form.title} onChange={e => set('title', e.target.value)} placeholder="Procurement Manager" />
                  </div>
                </div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'1rem' }}>
                  <div className="form-group">
                    <label className="form-label">Company</label>
                    <input value={form.company} onChange={e => set('company', e.target.value)} placeholder="Cameco Corporation" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Company Type</label>
                    <select value={form.company_type} onChange={e => set('company_type', e.target.value)}>
                      {COMPANY_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                  </div>
                </div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'1rem' }}>
                  <div className="form-group">
                    <label className="form-label">Email</label>
                    <input type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="jane@company.com" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Phone</label>
                    <input value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="+1 (306) 555-0100" />
                  </div>
                </div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'1rem' }}>
                  <div className="form-group">
                    <label className="form-label">Province</label>
                    <input value={form.province} onChange={e => set('province', e.target.value)} placeholder="SK" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Relationship</label>
                    <select value={form.relationship} onChange={e => set('relationship', e.target.value)}>
                      {Object.entries(RELATIONSHIP_STYLES).map(([v,s]) => <option key={v} value={v}>{s.label}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Role Type</label>
                    <select value={form.role_type} onChange={e => set('role_type', e.target.value)}>
                      {ROLE_TYPES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                    </select>
                  </div>
                </div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'1rem' }}>
                  <div className="form-group">
                    <label className="form-label">Last Contact Date</label>
                    <input type="date" value={form.last_contact} onChange={e => set('last_contact', e.target.value)} />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Notes</label>
                  <textarea value={form.notes} onChange={e => set('notes', e.target.value)} rows={3} placeholder="Key info, relationship context, follow-up items…" />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving…' : 'Add Contact'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function PlusIcon()   { return <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>; }
function PeopleIcon() { return <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>; }
